import { useEffect, useState } from "react";
import { X, Lock, Map as MapIcon, Tornado, ListChecks } from "lucide-react";
import { RISK } from "@/lib/constants";

export const ROOM_SCENES = [
  { id: "map", label: "Island map", Icon: MapIcon },
  { id: "storms", label: "Storms", Icon: Tornado },
  { id: "ledger", label: "Readiness ledger", Icon: ListChecks },
];
export const ROOM_SECONDS = 30;

function Clock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  const f = (o) => now.toLocaleString("en-GB", { timeZone: "America/Barbados", ...o });
  return (
    <div data-testid="room-clock" className="text-right">
      <div className="font-mono-t text-3xl font-bold text-white tabular-nums">{f({ hour: "2-digit", minute: "2-digit", second: "2-digit" })}</div>
      <div className="font-mono-t text-[11px] uppercase tracking-widest text-slate-300">{f({ weekday: "long", day: "numeric", month: "short" })} · AST</div>
    </div>
  );
}

export function RoomHud({ scene, left, locked, threat, threatColor, onExit, storm }) {
  return (
    <div data-testid="command-room-hud" className="absolute top-0 inset-x-0 z-[800] p-5 flex items-start gap-5 pointer-events-none">
      <div className="bg-white/95 rounded-2xl px-4 py-2 shadow-2xl border-b-2 border-cyan">
        <img src="/logo_full.png" alt="Sentinel Barbados" className="h-16 w-auto block" />
      </div>
      <div className="glass rounded-2xl px-5 py-3 border shadow-2xl" style={{ borderColor: `${threatColor}99` }}>
        <div className="font-mono-t text-[10px] uppercase tracking-[0.3em] text-slate-400">Threat level</div>
        <div data-testid="room-threat" className="font-heading text-4xl font-bold uppercase" style={{ color: threatColor }}>{threat}</div>
        {locked && storm && (
          <div data-testid="room-locked-storm" className="font-mono-t text-xs text-risk-critical mt-1">
            {storm.class_label} {storm.name} · {Math.round(storm.distance_km)} km
            {storm.eta_hours != null && ` · closest in ~${Math.round(storm.eta_hours)} h`}
          </div>
        )}
      </div>
      <div className="flex-1 flex flex-col items-center gap-2 pt-1">
        <div className="flex gap-2">
          {ROOM_SCENES.map(({ id, label, Icon }) => (
            <span key={id} data-testid={`room-scene-${id}`} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border font-heading uppercase tracking-wide text-sm ${
              scene === id ? "bg-cyan text-[#012173] border-cyan" : "glass text-slate-300 border-border"}`}>
              <Icon className="w-4 h-4" /> {label}
            </span>
          ))}
        </div>
        {locked ? (
          <div data-testid="room-locked" className="flex items-center gap-2 px-3 py-1 rounded-full bg-risk-critical/20 border border-risk-critical text-risk-critical font-mono-t text-xs uppercase animate-pulse">
            <Lock className="w-3.5 h-3.5" /> Locked on approaching storm
          </div>
        ) : (
          <div className="w-72 h-1 rounded-full bg-slate-800 overflow-hidden">
            <div data-testid="room-progress" className="h-full bg-cyan transition-[width] duration-1000 ease-linear" style={{ width: `${(1 - left / ROOM_SECONDS) * 100}%` }} />
          </div>
        )}
      </div>
      <Clock />
      <button data-testid="room-exit-button" onClick={onExit}
        className="pointer-events-auto glass rounded-full p-2.5 border border-border text-slate-300 hover:text-white hover:border-cyan transition-colors">
        <X className="w-5 h-5" />
      </button>
    </div>
  );
}

const Big = ({ label, value, color }) => (
  <div className="glass rounded-2xl p-5 border" style={{ borderColor: `${color}66` }}>
    <div className="font-mono-t text-xs uppercase tracking-widest text-slate-400">{label}</div>
    <div className="font-heading text-6xl font-bold tabular-nums" style={{ color }}>{value}</div>
  </div>
);

export function LedgerScene({ ledger, readiness, isolated, assets }) {
  const t = ledger?.tiers || {};
  const top = assets.filter((a) => a.tier === "critical" || a.tier === "high").slice(0, 10);
  return (
    <div data-testid="room-ledger-scene" className="absolute inset-x-0 top-0 bottom-9 z-[790] bg-[#020b24]/80 backdrop-blur-sm flex items-center justify-center p-10 pt-32 fade-up">
      <div className="w-full max-w-6xl space-y-5">
        <div className="grid grid-cols-4 gap-4">
          {["critical", "high", "medium", "low"].map((k) => <Big key={k} label={RISK[k].label} value={t[k] ?? 0} color={RISK[k].color} />)}
        </div>
        <div className="grid grid-cols-3 gap-4">
          <Big label="Readiness" value={`${readiness}%`} color="#FCC109" />
          <Big label="Isolated shelters" value={isolated} color={isolated ? "#FF3B30" : "#22C55E"} />
          <Big label="Assets monitored" value={assets.length} color="#e2e8f0" />
        </div>
        <div className="glass rounded-2xl p-5 border border-border">
          <div className="font-heading uppercase tracking-wide text-lg text-slate-100 mb-2">Assets at risk</div>
          {top.length === 0 ? <div className="font-mono-t text-base text-emerald-400">No asset is flooded now or forecast to flood in the next 72 hours.</div> : (
            <div className="grid grid-cols-2 gap-x-8 gap-y-1.5">
              {top.map((a) => (
                <div key={a.id} className="flex items-center gap-2 font-mono-t text-sm text-slate-200">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: RISK[a.tier].color }} />{a.name} <span className="text-slate-500">· {a.asset_type}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function StormScene({ storms, threat }) {
  const atl = (storms || []).filter((s) => s.in_atlantic);
  return (
    <div data-testid="room-storm-scene" className="absolute right-6 bottom-6 z-[790] w-[520px] glass rounded-2xl p-5 border border-cyan/30 shadow-2xl fade-up">
      <div className="font-heading uppercase tracking-wide text-xl text-slate-100 mb-3 flex items-center gap-2"><Tornado className="w-5 h-5 text-cyan" /> Atlantic storms · NOAA NHC</div>
      {atl.length === 0 && <div data-testid="room-no-storms" className="font-mono-t text-base text-emerald-400">No active Atlantic storms.</div>}
      {atl.map((s) => (
        <div key={s.id} className={`rounded-xl p-3 mb-2 border ${threat?.id === s.id ? "border-risk-critical bg-risk-critical/10" : "border-border"}`}>
          <div className="flex items-baseline justify-between">
            <span className="font-heading text-2xl font-bold text-white">{s.class_label} {s.name}</span>
            <span className="font-mono-t text-lg text-cyan">{s.intensity_kt} kt</span>
          </div>
          <div className="font-mono-t text-sm text-slate-300">
            {Math.round(s.distance_km)} km from Barbados{s.approaching ? " · APPROACHING" : ""}
            {s.eta_hours != null && ` · closest in ~${Math.round(s.eta_hours)} h (${Math.round(s.closest_approach_km)} km)`}
            {s.barbados_in_cone && " · Barbados in cone"}
          </div>
        </div>
      ))}
    </div>
  );
}
